import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { inventoryAPI } from '../../services/api';
import { countries } from '../../utils/countries';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorAlert from '../../components/ErrorAlert';
import SuccessAlert from '../../components/SuccessAlert';
import { FiSave, FiX, FiArrowLeft } from 'react-icons/fi';
import { useNotification } from '../../contexts/NotificationContext';

const InventoryEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  
  // Form state - only editable fields for customers
  const [formData, setFormData] = useState({
    productSku: '',
    internalSku: '',
    productName: '',
    packagingType: '',
    vendor: '',
    // Individual item dimensions/weight
    length: '',
    width: '',
    height: '',
    weightLbs: '',
    // Carton dimensions/weight
    cartonLength: '',
    cartonWidth: '',
    cartonHeight: '',
    cartonWeight: '',
    cartonUnitOfMeasurement: 'Each',
    unitsPerCarton: '',
    // Additional fields customers can edit
    value: '',
    upc: '',
    alternatePartNumber: '',
    hsCode: '',
    countryOfOrigin: '',
    description: '',
    notes: '',
    // Additional fields now available from complete API response
    category: '',
    colour: '',
    unitOfMeasurement: 'Each',
    boxQuantity: '',
    palletQuantity: '',
    minQuantity: '',
    maxQuantity: '',
    reorderThreshold: '',
    hazmatFlag: false,
    expiryDate: ''
  });
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const populateFormData = (item) => {
    if (!item) return;

    setFormData({
      productSku: item.productSku || '',
      internalSku: item.internalSku || '',
      productName: item.productName || item.productSku || '',
      packagingType: item.packagingType || '',
      vendor: item.vendor || '',
      length: item.length || '',
      width: item.width || '',
      height: item.height || '',
      weightLbs: item.weightLbs || '',
      cartonLength: item.cartonLength || '',
      cartonWidth: item.cartonWidth || '',
      cartonHeight: item.cartonHeight || '',
      cartonWeight: item.cartonWeight || '',
      cartonUnitOfMeasurement: item.cartonUnitOfMeasurement || 'Each',
      unitsPerCarton: item.unitsPerCarton || '',
      value: item.value || '',
      upc: item.upc || '',
      alternatePartNumber: item.alternatePartNumber || '',
      hsCode: item.hsCode || '',
      countryOfOrigin: item.countryOfOrigin || '',
      description: item.description || '',
      notes: item.notes || '',
      category: item.category || '',
      colour: item.colour || '',
      unitOfMeasurement: item.unitOfMeasurement || 'Each',
      boxQuantity: item.boxQuantity || '',
      palletQuantity: item.palletQuantity || '',
      minQuantity: item.minQuantity || '',
      maxQuantity: item.maxQuantity || '',
      reorderThreshold: item.reorderThreshold || '',
      hazmatFlag: item.hazmatFlag || false,
      expiryDate: item.expiryDate ? item.expiryDate.split('T')[0] : ''
    });
  };

  // Fetch existing inventory item
  const { data: inventoryData, isLoading: inventoryLoading } = useQuery(
    ['customerInventoryItem', id],
    () => inventoryAPI.getInventoryItem(id),
    {
      enabled: !!id,
      onError: (error) => {
        setError('Failed to load inventory item details');
        console.error('Error loading inventory item:', error);
      }
    }
  );

  useEffect(() => {
    populateFormData(inventoryData);
  }, [inventoryData]);

  // Update inventory item mutation
  const updateMutation = useMutation(
    (updateData) => inventoryAPI.updateInventory(id, updateData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['customerInventoryItem', id]);
        queryClient.invalidateQueries(['customer-inventory']);
        showSuccess('Inventory item updated successfully!');
        navigate(`/customer/inventory/${id}`);
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || 'Failed to update inventory item';
        showError(errorMessage);
        setError(errorMessage);
      }
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSaving(true);

    try {
      // Only send the fields that customers are allowed to edit
      const updateData = {
        product_sku: formData.productSku,
        internal_sku: formData.internalSku || null,
        product_name: formData.productName,
        packaging_type: formData.packagingType,
        unit_of_measurement: formData.unitOfMeasurement || null,
        // Individual dimensions
        length: formData.length ? parseFloat(formData.length) : null,
        width: formData.width ? parseFloat(formData.width) : null,
        height: formData.height ? parseFloat(formData.height) : null,
        weight_lbs: formData.weightLbs ? parseFloat(formData.weightLbs) : null,
        // Carton dimensions
        carton_length: formData.cartonLength ? parseFloat(formData.cartonLength) : null,
        carton_width: formData.cartonWidth ? parseFloat(formData.cartonWidth) : null,
        carton_height: formData.cartonHeight ? parseFloat(formData.cartonHeight) : null,
        carton_weight: formData.cartonWeight ? parseFloat(formData.cartonWeight) : null,
        carton_unit_of_measurement: formData.cartonUnitOfMeasurement,
        units_per_carton: formData.unitsPerCarton ? parseInt(formData.unitsPerCarton) : null,
        // Additional fields
        value: formData.value ? parseFloat(formData.value) : null,
        upc: formData.upc || null,
        alternate_part_number: formData.alternatePartNumber || null,
        hs_code: formData.hsCode || null,
        country_of_origin: formData.countryOfOrigin || null,
        description: formData.description || null,
        notes: formData.notes || null,
        vendor: formData.vendor || null
      };

      await updateMutation.mutateAsync(updateData);
    } catch (error) {
      console.error('Error updating inventory item:', error);
    } finally {
      setSaving(false);
    }
  };

  if (inventoryLoading) return <LoadingSpinner />;
  if (!inventoryData) return <ErrorAlert message="Inventory item not found." />;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Edit Inventory Item
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Update product information and specifications
                </p>
              </div>
              <button
                onClick={() => navigate(`/customer/inventory/${id}`)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <FiArrowLeft className="w-4 h-4 mr-2" />
                Back to Details
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="px-6 py-6 space-y-6">
            {error && <ErrorAlert message={error} />}
            {success && <SuccessAlert message={success} />}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="productSku" className="block text-sm font-medium text-gray-700 mb-2">
                  Product SKU *
                </label>
                <input
                  type="text"
                  id="productSku"
                  name="productSku"
                  value={formData.productSku}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label htmlFor="productName" className="block text-sm font-medium text-gray-700 mb-2">
                  Product Name *
                </label>
                <input
                  type="text"
                  id="productName"
                  name="productName"
                  value={formData.productName}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="internalSku" className="block text-sm font-medium text-gray-700 mb-2">
                  Internal SKU (Optional)
                </label>
                <input
                  type="text"
                  id="internalSku"
                  name="internalSku"
                  value={formData.internalSku}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label htmlFor="packagingType" className="block text-sm font-medium text-gray-700 mb-2">
                  Manufacturer SKU (Optional)
                </label>
                <input
                  type="text"
                  id="alternatePartNumber"
                  name="alternatePartNumber"
                  value={formData.alternatePartNumber}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Product Description (Optional)
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div>
              <label htmlFor="vendor" className="block text-sm font-medium text-gray-700 mb-2">
                Vendor
              </label>
              <input
                type="text"
                id="vendor"
                name="vendor"
                value={formData.vendor}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="packagingType" className="block text-sm font-medium text-gray-700 mb-2">
                  Packaging Type *
                </label>
                <select
                  id="packagingType"
                  name="packagingType"
                  value={formData.packagingType}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select Packaging Type</option>
                  <option value="individual">Individual Units</option>
                  <option value="carton">Carton</option>
                </select>
              </div>
              <div>
                <label htmlFor="unitOfMeasurement" className="block text-sm font-medium text-gray-700 mb-2">
                  Unit of Measurement
                </label>
                <select
                  id="unitOfMeasurement"
                  name="unitOfMeasurement"
                  value={formData.unitOfMeasurement}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="Each">Each</option>
                  <option value="Box">Box</option>
                  <option value="Pallet">Pallet</option>
                </select>
              </div>
            </div>

            {formData.packagingType && (
              <div className="rounded-lg border border-gray-200 p-4">
                <h4 className="text-sm font-semibold text-gray-900">Item Details</h4>
                <div className="mt-4 space-y-6">
                  <div>
                    <label htmlFor="weightLbs" className="block text-sm font-medium text-gray-700 mb-2">
                      Item Weight (lbs)
                    </label>
                    <input
                      type="number"
                      id="weightLbs"
                      name="weightLbs"
                      value={formData.weightLbs}
                      onChange={handleChange}
                      step="0.01"
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Item Dimensions (inches)
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label htmlFor="length" className="block text-sm font-medium text-gray-700 mb-1">
                          Item Length (inches)
                        </label>
                        <input
                          type="number"
                          id="length"
                          name="length"
                          value={formData.length}
                          onChange={handleChange}
                          step="0.01"
                          min="0"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                      <div>
                        <label htmlFor="width" className="block text-sm font-medium text-gray-700 mb-1">
                          Item Width (inches)
                        </label>
                        <input
                          type="number"
                          id="width"
                          name="width"
                          value={formData.width}
                          onChange={handleChange}
                          step="0.01"
                          min="0"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                      <div>
                        <label htmlFor="height" className="block text-sm font-medium text-gray-700 mb-1">
                          Item Height (inches)
                        </label>
                        <input
                          type="number"
                          id="height"
                          name="height"
                          value={formData.height}
                          onChange={handleChange}
                          step="0.01"
                          min="0"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {formData.packagingType === 'carton' && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <h4 className="text-sm font-semibold text-gray-900">Carton Details</h4>
                <div className="mt-4 space-y-6">
                <div>
                  <label htmlFor="cartonWeight" className="block text-sm font-medium text-gray-700 mb-2">
                    Carton Weight (lbs)
                  </label>
                  <input
                    type="number"
                    id="cartonWeight"
                    name="cartonWeight"
                    value={formData.cartonWeight}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Carton Dimensions (inches)
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div>
                      <label htmlFor="length" className="block text-sm font-medium text-gray-700 mb-1">
                        Carton Length (inches)
                      </label>
                      <input
                        type="number"
                        id="cartonLength"
                        name="cartonLength"
                        value={formData.cartonLength}
                        onChange={handleChange}
                        step="0.01"
                        min="0"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <div>
                    <label htmlFor="cartonWidth" className="block text-sm font-medium text-gray-700 mb-1">
                      Carton Width (inches)
                    </label>
                    <input
                      type="number"
                      id="cartonWidth"
                      name="cartonWidth"
                      value={formData.cartonWidth}
                      onChange={handleChange}
                      step="0.01"
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="cartonHeight" className="block text-sm font-medium text-gray-700 mb-1">
                      Carton Height (inches)
                    </label>
                    <input
                      type="number"
                      id="cartonHeight"
                      name="cartonHeight"
                      value={formData.cartonHeight}
                      onChange={handleChange}
                      step="0.01"
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="cartonUnitOfMeasurement" className="block text-sm font-medium text-gray-700 mb-1">
                      Carton UOM
                    </label>
                    <select
                      id="cartonUnitOfMeasurement"
                      name="cartonUnitOfMeasurement"
                      value={formData.cartonUnitOfMeasurement}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="Each">Each</option>
                      <option value="Case">Case</option>
                      <option value="Pack">Pack</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="unitsPerCarton" className="block text-sm font-medium text-gray-700 mb-1">
                      Qty per Carton
                    </label>
                    <input
                      type="number"
                      id="unitsPerCarton"
                      name="unitsPerCarton"
                      value={formData.unitsPerCarton}
                      onChange={handleChange}
                      min="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  </div>
                </div>
              </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="value" className="block text-sm font-medium text-gray-700 mb-2">
                  Approximate Value ($) (per item)
                </label>
                <input
                  type="number"
                  id="value"
                  name="value"
                  value={formData.value}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="upc" className="block text-sm font-medium text-gray-700 mb-2">
                  UPC / EAN / GTIN
                </label>
                <input
                  type="text"
                  id="upc"
                  name="upc"
                  value={formData.upc}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label htmlFor="hsCode" className="block text-sm font-medium text-gray-700 mb-2">
                  HS Code
                </label>
                <input
                  type="text"
                  id="hsCode"
                  name="hsCode"
                  value={formData.hsCode}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            <div>
              <label htmlFor="countryOfOrigin" className="block text-sm font-medium text-gray-700 mb-2">
                Country of Origin
              </label>
              <select
                id="countryOfOrigin"
                name="countryOfOrigin"
                value={formData.countryOfOrigin}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Select Country</option>
                {countries.map((country) => (
                  <option key={country.code} value={country.name}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate(`/customer/inventory/${id}`)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <FiX className="w-4 h-4 mr-2" />
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiSave className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InventoryEdit;
